<template>
  <div class="time-series-chart">
    <VueApexCharts
        type="area"
        height="380"
        :options="chartOptions"
        :series="series"
    />
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";

export default {
  name: 'TimeSeriesChart',

  components: { VueApexCharts },

  props: {
    data: {
      type: Array,
      default: () => []
    },
    filters: {
      type: Object,
      default: () => ({})
    }
  },

  computed: {
    series() {
      return [
        {
          name: 'Lançamentos',
          data: this.data.map(d => d.lancamentos)
        },
        {
          name: 'Divergências',
          data: this.data.map(d => d.divergencias)
        }
      ];
    },

    chartOptions() {
      return {
        chart: {
          type: 'area',
          height: 380,
          fontFamily: 'Inter, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
          toolbar: {
            show: false,
            offsetY: -5,
            tools: {
              download: true,
              selection: false,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            },
            export: {
              csv: {
                filename: 'lancamentos-historico',
                headerCategory: 'Data',
              },
              svg: {
                filename: 'lancamentos-historico',
              },
              png: {
                filename: 'lancamentos-historico',
              }
            }
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800,
            animateGradually: {
              enabled: true,
              delay: 150
            },
            dynamicAnimation: {
              enabled: true,
              speed: 350
            }
          },
          background: 'transparent',
          zoom: {
            enabled: true,
            type: 'x',
            autoScaleYaxis: true
          }
        },

        colors: ['#023449', '#f57c00'],

        dataLabels: {
          enabled: false
        },

        stroke: {
          curve: 'smooth',
          width: 3,
          lineCap: 'round'
        },

        fill: {
          type: 'gradient',
          gradient: {
            shade: 'light',
            type: 'vertical',
            shadeIntensity: 0.5,
            gradientToColors: ['#034560', '#fb8c00'],
            inverseColors: false,
            opacityFrom: 0.6,
            opacityTo: 0.1,
            stops: [0, 90, 100]
          }
        },

        markers: {
          size: 0,
          colors: ['#023449', '#f57c00'],
          strokeColors: '#ffffff',
          strokeWidth: 2,
          hover: {
            size: 6,
            sizeOffset: 3
          }
        },

        xaxis: {
          type: 'category',
          categories: this.data.map(d => {
            const date = new Date(d.data);
            return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
          }),
          labels: {
            style: {
              colors: '#64748b',
              fontSize: '12px',
              fontWeight: 500
            },
            rotate: -45,
            rotateAlways: false,
            hideOverlappingLabels: true,
            trim: false
          },
          axisBorder: {
            show: true,
            color: '#e2e8f0',
            height: 1
          },
          axisTicks: {
            show: true,
            color: '#e2e8f0',
            height: 6
          },
          tooltip: {
            enabled: false
          }
        },

        yaxis: {
          title: {
            text: 'Quantidade',
            rotate: -90,
            offsetX: 0,
            offsetY: 0,
            style: {
              color: '#023449',
              fontSize: '13px',
              fontWeight: 700,
              cssClass: 'apexcharts-yaxis-title',
            }
          },
          labels: {
            style: {
              colors: '#64748b',
              fontSize: '12px',
              fontWeight: 500
            },
            formatter: (value) => {
              return Math.round(value).toLocaleString('pt-BR');
            }
          },
          min: 0,
          forceNiceScale: true
        },

        legend: {
          show: true,
          position: 'top',
          horizontalAlign: 'right',
          fontSize: '13px',
          fontWeight: 600,
          offsetY: 0,
          labels: {
            colors: '#023449',
            useSeriesColors: false
          },
          markers: {
            width: 12,
            height: 12,
            strokeWidth: 0,
            strokeColor: '#fff',
            radius: 3,
            offsetX: -3,
            offsetY: 0
          },
          itemMargin: {
            horizontal: 16,
            vertical: 0
          },
          onItemClick: {
            toggleDataSeries: true
          },
          onItemHover: {
            highlightDataSeries: true
          }
        },

        grid: {
          show: true,
          borderColor: '#e2e8f0',
          strokeDashArray: 3,
          position: 'back',
          xaxis: {
            lines: {
              show: false
            }
          },
          yaxis: {
            lines: {
              show: true
            }
          },
          padding: {
            top: 0,
            right: 10,
            bottom: 0,
            left: 10
          }
        },

        tooltip: {
          enabled: true,
          shared: true,
          intersect: false,
          theme: 'light',
          style: {
            fontSize: '13px',
            fontFamily: 'Inter, Segoe UI, Tahoma, Geneva, Verdana, sans-serif'
          },
          x: {
            show: true,
            format: 'dd/MM/yyyy',
            formatter: undefined
          },
          y: {
            formatter: (value) => `${value.toLocaleString('pt-BR')} itens`,
            title: {
              formatter: (seriesName) => seriesName + ':'
            }
          },
          marker: {
            show: true
          },
          fillSeriesColor: false,
          onDatasetHover: {
            highlightDataSeries: true
          }
        },

        states: {
          normal: {
            filter: {
              type: 'none',
              value: 0
            }
          },
          hover: {
            filter: {
              type: 'lighten',
              value: 0.1
            }
          },
          active: {
            allowMultipleDataPointsSelection: false,
            filter: {
              type: 'darken',
              value: 0.5
            }
          }
        },

        responsive: [
          {
            breakpoint: 768,
            options: {
              chart: {
                height: 300
              },
              legend: {
                position: 'bottom',
                horizontalAlign: 'center',
                offsetY: 5
              },
              xaxis: {
                labels: {
                  rotate: -90,
                  style: {
                    fontSize: '10px'
                  }
                }
              },
              yaxis: {
                labels: {
                  style: {
                    fontSize: '10px'
                  }
                }
              }
            }
          },
          {
            breakpoint: 480,
            options: {
              chart: {
                height: 250
              },
              xaxis: {
                labels: {
                  show: false
                }
              }
            }
          }
        ]
      };
    }
  }
};
</script>

<style scoped>
.time-series-chart {
  width: 100%;
  height: 100%;
  background: #ffffff;
  border-radius: 12px;
  padding: 16px;
}

/* Customização adicional dos estilos do ApexCharts */
.time-series-chart >>> .apexcharts-toolbar {
  z-index: 11;
}

.time-series-chart >>> .apexcharts-menu {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(2, 52, 73, 0.12);
}

.time-series-chart >>> .apexcharts-menu-item {
  color: #023449;
  font-size: 13px;
  padding: 8px 12px;
}

.time-series-chart >>> .apexcharts-menu-item:hover {
  background: rgba(2, 52, 73, 0.08);
}

.time-series-chart >>> .apexcharts-tooltip {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(2, 52, 73, 0.12);
}

.time-series-chart >>> .apexcharts-tooltip-title {
  background: rgba(2, 52, 73, 0.04);
  border-bottom: 1px solid #e2e8f0;
  color: #023449;
  font-weight: 700;
  padding: 8px 12px;
  font-size: 13px;
}

.time-series-chart >>> .apexcharts-tooltip-series-group {
  padding: 6px 12px;
}

.time-series-chart >>> .apexcharts-tooltip-marker {
  width: 10px;
  height: 10px;
  border-radius: 2px;
}

.time-series-chart >>> .apexcharts-tooltip-text-y-label {
  color: #64748b;
  font-weight: 500;
}

.time-series-chart >>> .apexcharts-tooltip-text-y-value {
  color: #023449;
  font-weight: 700;
  margin-left: 4px;
}

.time-series-chart >>> .apexcharts-legend-text {
  color: #023449 !important;
}

.time-series-chart >>> .apexcharts-yaxis-label,
.time-series-chart >>> .apexcharts-xaxis-label {
  fill: #64748b;
}

.time-series-chart >>> .apexcharts-gridline {
  stroke: #e2e8f0;
}

/* Animação de entrada */
.time-series-chart {
  animation: fadeIn 0.5s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Acessibilidade - redução de movimento */
@media (prefers-reduced-motion: reduce) {
  .time-series-chart {
    animation: none;
  }
}
</style>